'use client';

import { motion } from 'motion/react';
import { Calendar, Mail, Phone, MessageSquare, ArrowRight, Sparkles } from 'lucide-react';
import { useState } from 'react';

import type { CTAData } from '../lib/parser';

// ============================================================================
// Types
// ============================================================================

interface CTACardProps {
    cta: CTAData;
    onContactSubmit?: (contactType: string, contactValue: string) => void;
    onCTAClick?: (ctaType: string, url: string) => void;
}

// ============================================================================
// Icon Map
// ============================================================================

const CTA_ICONS: Record<string, React.ReactNode> = {
    discovery: <Calendar className="w-5 h-5" />,
    audit: <Sparkles className="w-5 h-5" />,
    contact: <Mail className="w-5 h-5" />,
    quote: <MessageSquare className="w-5 h-5" />,
    book: <Calendar className="w-5 h-5" />,
};

const CTA_COLORS: Record<string, { bg: string; border: string; accent: string }> = {
    discovery: { bg: 'from-blue-500/10 to-indigo-500/10', border: 'border-blue-500/30', accent: 'text-blue-400' },
    audit: { bg: 'from-violet-500/10 to-purple-500/10', border: 'border-violet-500/30', accent: 'text-violet-400' },
    contact: { bg: 'from-emerald-500/10 to-teal-500/10', border: 'border-emerald-500/30', accent: 'text-emerald-400' },
    quote: { bg: 'from-amber-500/10 to-orange-500/10', border: 'border-amber-500/30', accent: 'text-amber-400' },
    book: { bg: 'from-rose-500/10 to-pink-500/10', border: 'border-rose-500/30', accent: 'text-rose-400' },
};

// ============================================================================
// Contact Input Component
// ============================================================================

function ContactInput({
    onSubmit,
}: {
    onSubmit: (type: string, value: string) => void;
}) {
    const [contactType, setContactType] = useState<'email' | 'phone'>('email');
    const [contactValue, setContactValue] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async () => {
        if (!contactValue.trim()) return;

        setIsSubmitting(true);
        try {
            await onSubmit(contactType, contactValue);
            setSubmitted(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-emerald-400 text-sm"
            >
                <span className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    ✓
                </span>
                Thank you! We&apos;ll be in touch shortly.
            </motion.div>
        );
    }

    return (
        <div className="space-y-3">
            {/* Contact Type Toggle */}
            <div className="flex gap-2">
                <button
                    onClick={() => setContactType('email')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${contactType === 'email'
                            ? 'bg-white/10 text-white'
                            : 'text-white/50 hover:text-white/70'
                        }`}
                >
                    <Mail className="w-3.5 h-3.5" />
                    Email
                </button>
                <button
                    onClick={() => setContactType('phone')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${contactType === 'phone'
                            ? 'bg-white/10 text-white'
                            : 'text-white/50 hover:text-white/70'
                        }`}
                >
                    <Phone className="w-3.5 h-3.5" />
                    Phone
                </button>
            </div>

            {/* Input Field */}
            <div className="flex gap-2">
                <input
                    type={contactType === 'email' ? 'email' : 'tel'}
                    placeholder={contactType === 'email' ? 'your@email.com' : '+1 234 567 890'}
                    value={contactValue}
                    onChange={(e) => setContactValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                />
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSubmit}
                    disabled={isSubmitting || !contactValue.trim()}
                    className="px-4 py-2.5 bg-white text-black rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/90"
                >
                    {isSubmitting ? '...' : 'Submit'}
                </motion.button>
            </div>

            <p className="text-xs text-white/30">
                Your contact info is only shared with Hamid, never with AI.
            </p>
        </div>
    );
}

// ============================================================================
// Main CTA Card Component
// ============================================================================

export function CTACard({ cta, onContactSubmit, onCTAClick }: CTACardProps) {
    const colors = CTA_COLORS[cta.ctaType] || CTA_COLORS.contact;
    const icon = CTA_ICONS[cta.ctaType] || CTA_ICONS.contact;

    const handleClick = () => {
        if (cta.collectContact) return; // Don't navigate if collecting contact

        if (onCTAClick) {
            onCTAClick(cta.ctaType, cta.url);
        }

        // Open in new tab if external, otherwise navigate
        if (cta.url.startsWith('http')) {
            window.open(cta.url, '_blank');
        } else {
            window.location.href = cta.url;
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className={`
                relative overflow-hidden rounded-xl border backdrop-blur-sm
                bg-gradient-to-br ${colors.bg} ${colors.border}
                p-4 my-3
            `}
        >
            {/* Shimmer Effect */}
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/5 to-transparent" />

            {/* Header */}
            <div className="flex items-start gap-3 mb-3">
                <div className={`p-2 rounded-lg bg-white/5 ${colors.accent}`}>
                    {icon}
                </div>
                <div className="flex-1">
                    <h4 className="font-semibold text-white text-sm">
                        {cta.label}
                    </h4>
                    {cta.description && (
                        <p className="text-white/60 text-xs mt-0.5 leading-relaxed">
                            {cta.description}
                        </p>
                    )}
                </div>
                {cta.price && (
                    <span className={`text-sm font-medium ${colors.accent}`}>
                        {cta.price}
                    </span>
                )}
            </div>

            {/* Action */}
            {cta.collectContact ? (
                <ContactInput onSubmit={onContactSubmit || (() => { })} />
            ) : (
                <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={handleClick}
                    className={`
                        w-full flex items-center justify-center gap-2 
                        px-4 py-2.5 rounded-lg
                        bg-white text-black font-medium text-sm
                        hover:bg-white/90 transition-colors
                    `}
                >
                    {cta.action}
                    <ArrowRight className="w-4 h-4" />
                </motion.button>
            )}
        </motion.div>
    );
}

export default CTACard;
